import { IonPage, useIonViewWillEnter } from '@ionic/react';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { setActiveTab } from 'src/redux/tabBar/reducer';
import { setIsActive } from 'src/redux/tabBar/reducer';
import { useDispatch, useSelector } from 'react-redux';
import { Tabs } from 'src/redux/tabBar/reducer';
import { PublicRoutes } from 'src/routes/navigation';
import { useSwipeBackGesture } from 'src/shared/hooks/useSwipeBackGesture';
import { TranslatedSessionPaymentsWidget } from 'src/translated-components';
import { userSelector } from 'src/redux/user/selector';
import { RouteComponentProps, useHistory } from 'react-router-dom';
import { consultationsByIDSelector } from 'src/redux/consultations/selector';
import { CardModal, Container_v2 as Container, Loading, Session } from '@mindly/ui-components';
import { getConsultationByID } from 'src/api/consultation';
import { getUsersPsychologistsListThunk } from 'src/redux/usersPsychologists/action';
import { usersPsychologistsSelector } from 'src/redux/usersPsychologists/selector';
import { fetchSubscriptions } from 'src/redux/subscriptions/thunk';
import { useTranslation } from 'react-i18next';
import useToast from 'src/context/ToastContext/ToastContext';
import { openDocumentFile } from 'src/shared/utils/documents';
import { SupportedDocumentType } from 'src/shared/types/documents.type';
import { TypeDocument } from 'src/shared/types/documents';

interface SessionDetailProps
  extends RouteComponentProps<{
    id: string;
  }> {}

const SessionPayments: React.FC<SessionDetailProps> = ({ match }) => {
  const pageRef = useRef();
  const dispatch = useDispatch();
  const user = useSelector(userSelector);
  const history = useHistory();
  const sessionData = useSelector(consultationsByIDSelector(match.params.id));
  const [session, setSession] = useState<Session | undefined>(sessionData as unknown as Session);
  const [isLoading, setIsLoading] = useState(!sessionData);
  const usersPsychologistList = useSelector(usersPsychologistsSelector);
  const [isDocumentLoading, setIsDocumentLoading] = useState(false);
  const { t } = useTranslation('ui');
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const { showToast } = useToast();

  useSwipeBackGesture(pageRef);

  useIonViewWillEnter(() => {
    if (!user?.id) {
      history.push(PublicRoutes.AUTH);
      return;
    }

    dispatch(setIsActive(false));
    dispatch(setActiveTab(Tabs.second));
  }, [user?.id]);

  const fetchData = useCallback(async () => {
    try {
      if (sessionData && usersPsychologistList) {
        setSession(sessionData as unknown as Session);
        return;
      }
      setIsLoading(true);
      const [fetchedSessionData] = await Promise.all([
        getConsultationByID(match.params.id),
        dispatch(fetchSubscriptions(user?.id)),
        dispatch(getUsersPsychologistsListThunk()),
      ]);
      setSession(fetchedSessionData as unknown as Session);
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  }, [match.params.id, sessionData, usersPsychologistList]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleBackClick = useCallback(() => {
    history.back();
  }, [history]);

  const openDocument = useCallback(
    async (type: TypeDocument) => {
      try {
        if (!session?.id) return;
        setIsDocumentLoading(true);
        await openDocumentFile(session.id, type, SupportedDocumentType.PDF);
      } catch (error) {
        showToast({ text: t('session_payments.document_not_ready') });
      } finally {
        setIsDocumentLoading(false);
      }
    },
    [session?.id]
  );

  const handleInvoiceClick = useCallback(() => {
    openDocument('invoice');
  }, [openDocument]);

  const handleReceiptPaymentClick = useCallback(() => {
    openDocument('receipt-payment');
  }, [openDocument]);

  const handleRelatedSessionsClick = useCallback(() => {
    openDocument('related-sessions');
  }, [openDocument]);

  return (
    <IonPage ref={pageRef}>
      <TranslatedSessionPaymentsWidget
        onBackClick={handleBackClick}
        isLoading={isLoading}
        onInvoiceClick={handleInvoiceClick}
        onPaymentReceiptClick={handleReceiptPaymentClick}
        onAllSessionsClick={handleRelatedSessionsClick}
        session={session}
      />
      <Loading isOpen={isDocumentLoading} />
      <CardModal isOpen={!!pdfUrl} onDidDismiss={() => setPdfUrl(null)}>
        <Container className="h-screen">
          {pdfUrl && <iframe src={pdfUrl} width="100%" className="h-full" title="PDF" />}
        </Container>
      </CardModal>
    </IonPage>
  );
};

export default SessionPayments;
